import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';

/** One program reached by the group being edited. */
export interface PugProgramAccessItem {
  programId: string;
  programName: string;
  statusLabel: string;
  /** Current saved state: is this user excluded from this program THROUGH THIS GROUP? */
  isExcluded: boolean;
  /**
   * Every OTHER group of this user's that is currently allowed on this program.
   *
   * A list, not one name: a user can reach a program through three groups, and flipping only the
   * first would leave a second conflict behind. On valid data there is at most one, since the
   * invariant allows one — so naming them all is free when things are healthy and repairs them
   * when they are not.
   */
  conflictingGroupNames: string[];
  /** Program is expired or inactive — listed for completeness, but nothing rides on it. */
  isDead: boolean;
}

export interface PugProgramAccessParams {
  programUserGroupName: string;
  userFullName: string;
  isReadOnly: boolean;
  items: PugProgramAccessItem[];
}

/** Program ids the user should be EXCLUDED from through this group, after editing. */
export interface PugProgramAccessResult {
  excludedProgramIds: string[];
  /**
   * Programs where this group is taking the active slot, so the caller must deactivate whichever
   * other groups hold it. Without this the save produces two active groups on one program, which
   * the rest of the application treats as a hard block — the program vanishes from Place an Order
   * and the user cannot order it at all.
   */
  switchingProgramIds: string[];
}

/**
 * The group-first view: one Program User Group, every program it reaches for this user, and a
 * checkbox per program to include or exclude them.
 *
 * Complements the program-first chooser (ProgramPugChooserDialogComponent): that one answers
 * "which of these groups is correct for this program", this one answers "which of this group's
 * programs should this user actually get". Both write the same ProgramUserGroupExclusion rows.
 */
@Component({
  selector: 'prox-pug-program-access-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule],
  template: `
    <div class="flex h-full w-full flex-col">
      <p class="mb-3 text-base">
        {{ userFullName }} is in <strong>{{ programUserGroupName }}</strong
        >, which reaches
        {{ items.length }} {{ items.length === 1 ? 'program' : 'programs' }}. Untick a program to
        exclude {{ firstName }} from it — the membership stays in place.
      </p>

      <div class="flex flex-col divide-y overflow-auto border-y">
        @for (item of items; track item.programId) {
          <div class="flex items-start gap-3 py-3">
            <p-checkbox
              [binary]="true"
              [(ngModel)]="included[item.programId]"
              [disabled]="isReadOnly"
              [inputId]="'inc-' + item.programId"></p-checkbox>
            <label class="flex cursor-pointer flex-col" [attr.for]="'inc-' + item.programId">
              <span class="font-medium" [class.text-gray-500]="item.isDead">{{ item.programName }}</span>
              <span class="text-sm text-gray-600">{{ item.statusLabel }}</span>
              <!-- Ticking this makes THIS group the active one and deactivates whichever group is
                   active now. That is a change in a place the administrator is not looking, so it
                   is spelled out here, before Save, with both buttons' consequences named. -->
              @if (willSwitch(item)) {
                <span class="mt-1 block rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900">
                  <strong>{{ otherGroups(item) }}</strong>
                  {{ item.conflictingGroupNames.length === 1 ? 'is' : 'are' }} currently active for
                  <strong>{{ item.programName }}</strong>. Only one Program User Group can be active
                  for a program at a time.
                  <span class="mt-1 block">
                    <strong>Cancel</strong> if you want {{ firstName }} to retain
                    {{ otherGroups(item) }}.
                  </span>
                  <span class="block">
                    <strong>Save</strong> if you wish to make <strong>{{ programUserGroupName }}</strong>
                    the active Program User Group for {{ firstName }} and deactivate
                    {{ otherGroups(item) }}.
                  </span>
                </span>
              }
            </label>
          </div>
        }
      </div>

      <div class="flex pt-4 items-center">
        <button
          pButton
          id="pugProgramAccessSave"
          [disabled]="isReadOnly || !hasChanged()"
          (click)="onSave()"
          label="Save"
          icon="pi pi-check"
          class="mr-2"></button>
        <button pButton (click)="onCancel()" label="Cancel" icon="pi pi-times" class="mr-2"></button>
        <div class="flex-grow"></div>
        <span class="text-sm text-gray-600">{{ includedCount() }} of {{ items.length }} included</span>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
    `,
  ],
})
export class PugProgramAccessDialogComponent implements OnInit {
  programUserGroupName = '';
  userFullName = '';
  firstName = '';
  isReadOnly = false;
  items: PugProgramAccessItem[] = [];

  /** programId -> included (the inverse of excluded, because a tick reading "yes" is clearer). */
  included: Record<string, boolean> = {};
  private initial: Record<string, boolean> = {};

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig<PugProgramAccessParams>,
  ) {}

  public static async openDialog(dialogService: DialogService, data: PugProgramAccessParams) {
    const ref = dialogService.open(PugProgramAccessDialogComponent, {
      closable: false,
      draggable: true,
      modal: true,
      width: '680px',
      header: data.programUserGroupName,
      data,
    });
    return (await firstValueFrom(ref.onClose)) as PugProgramAccessResult | undefined;
  }

  ngOnInit(): void {
    const params = this.config.data!;
    this.programUserGroupName = params.programUserGroupName;
    this.userFullName = params.userFullName;
    this.firstName = params.userFullName.split(' ')[0] || 'this user';
    this.isReadOnly = params.isReadOnly;
    this.items = params.items;

    this.items.forEach((i) => (this.included[i.programId] = !i.isExcluded));
    this.initial = { ...this.included };
  }

  /**
   * True when ticking this program would take the active slot from another group.
   *
   * Only when the tick is a CHANGE: a program already included with a standing conflict is a
   * pre-existing broken state, not something this edit is about to cause, and saying "Save will
   * deactivate X" about a box the administrator has not touched would be a lie.
   */
  willSwitch(item: PugProgramAccessItem): boolean {
    return this.included[item.programId]
        && !this.initial[item.programId]
        && item.conflictingGroupNames.length > 0;
  }

  /** The other active groups, named, so the warning never says "another group". */
  otherGroups(item: PugProgramAccessItem): string {
    const names = item.conflictingGroupNames;
    if (names.length <= 1) return names[0] ?? '';
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }

  /** Programs where saving will move the active slot to this group. */
  switchingItems(): PugProgramAccessItem[] {
    return this.items.filter((i) => this.willSwitch(i));
  }

  includedCount(): number {
    return this.items.filter((i) => this.included[i.programId]).length;
  }

  hasChanged(): boolean {
    return this.items.some((i) => this.included[i.programId] !== this.initial[i.programId]);
  }

  onSave(): void {
    const excludedProgramIds = this.items.filter((i) => !this.included[i.programId]).map((i) => i.programId);
    const switchingProgramIds = this.switchingItems().map((i) => i.programId);
    this.ref.close({ excludedProgramIds, switchingProgramIds } as PugProgramAccessResult);
  }

  onCancel(): void {
    this.ref.close();
  }
}
